/**
 * PWA install plumbing. Two jobs:
 *  1. Capture Chrome/Android's `beforeinstallprompt` so the app can show its OWN "Install"
 *     button (Chrome no longer pops a banner on its own — you must call prompt() yourself).
 *  2. Register a minimal service worker (required for a reliable install prompt) that is
 *     NETWORK-FIRST so it never serves a stale build, and never touches cross-origin traffic
 *     (the OpenMarkets API / logos go straight to the network, untouched).
 *
 * iOS Safari never fires `beforeinstallprompt` and can't be prompted — installing there is
 * always manual (Share → Add to Home Screen), so the UI shows a hint instead.
 */
import { useSyncExternalStore } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Prevent the mini-infobar; we surface our own install affordance instead.
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    emit();
  });
}

/** Re-renders when Chrome reports the app is installable (or after it's been installed). */
export function useCanInstall(): boolean {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => deferred !== null,
    () => false,
  );
}

/** Already launched from the home screen? Then there's nothing to install. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari's non-standard flag
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/** iOS / iPadOS — where install is manual (Share → Add to Home Screen), no prompt API. */
export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // iPadOS 13+ reports as Mac but has touch
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

/** Fire Chrome's native install prompt. No-op if we haven't captured one. */
export async function promptInstall(): Promise<void> {
  if (!deferred) return;
  const e = deferred;
  await e.prompt();
  try {
    await e.userChoice;
  } finally {
    deferred = null;
    emit();
  }
}

/** Register the network-first service worker (production only — avoids caching the dev server). */
export function registerServiceWorker(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  if (!import.meta.env.PROD) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      /* install prompt still works without it; nothing user-facing to do on failure */
    });
  });
}
